import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Sheet, SheetContent } from "../../components/ui/sheet";
import { Progress } from "../../components/ui/progress";
import {
  FileText, DollarSign, CheckCircle2, Clock, Upload, Package,
  Info, X, ReceiptText, Truck, Plus, ChevronDown,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Invoice } from "../../store/store";
import { formatCurrencyINR, getFinancialYear, parseNumericAmount } from "../../lib/enhanced-currency-utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";
import { getLocalStorageItem } from "../../utils/Validation";
import { DocThumb } from "../../pages/manage-post-disbursement/DocThumb";
import type { ShipmentDocFile } from "../../types/postDisbursement";
import {
  extractAssetShipmentDocumentsFromResponse,
  filterEwayBillDocuments,
  filterProformaInvoiceDocuments,
  filterTaxInvoiceDocuments,
} from "../../lib/assetShipmentDocuments";
import {
  extractManageInvoiceDocUrl,
  MANAGE_INVOICE_DOC_URL_TTL_MS,
} from "../../lib/manageInvoiceDoc";

interface ApiResponse<T> {
  data: T;
  status?: number;
  message?: string;
}

const normalizeInvoiceDocFiles = (files: unknown): ShipmentDocFile[] => {
  if (!Array.isArray(files)) return [];
  return files
    .map((raw): ShipmentDocFile | null => {
      if (!raw || typeof raw !== "object") return null;
      const doc = raw as Record<string, unknown>;
      const id = String(doc.id ?? "").trim();
      const name = String(doc.name ?? doc.filename ?? doc.file_name ?? "").trim();
      const url = String(doc.url ?? doc.s3_url ?? doc.fileUrl ?? doc.s3_file_key ?? "").trim();
      const updated_at = String(doc.updated_at ?? doc.created_at ?? "").trim();
      if (!id || !name || !url) return null;
      return {
        id,
        name,
        url,
        updated_at: updated_at || new Date().toISOString(),
      };
    })
    .filter((file): file is ShipmentDocFile => file !== null);
};

interface adminUserList {
  id: number;
  name: string;
}

const TDS_PURCHASE_THRESHOLD = 5_000_000; // ₹50 Lakh

function isEwayNotApplicable(status?: string | null): boolean {
  return String(status ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .trim() === "not applicable";
}

/** Taxable amount for TDS: if prior FY purchases already crossed threshold, only current invoice is taxable. */
function getTaxablePurchaseValue(invoice: Invoice): number {
  const previous = invoice.previousPurchaseValue || 0;
  if (previous >= TDS_PURCHASE_THRESHOLD) {
    return invoice.amount;
  }
  return Math.max(0, invoice.amount + previous - TDS_PURCHASE_THRESHOLD);
}

export default function InvoiceDetailDialog({ invoice, po, open, onOpenChange, addPayment, adminUserList }: { invoice: Invoice, po: any, open: boolean, onOpenChange: (open: boolean) => void, addPayment: () => void, adminUserList: adminUserList[] }) {
  return (
    <InvoiceViewSheet invoice={invoice} open={open} onOpenChange={onOpenChange} po={po} addPayment={addPayment} adminUserList={adminUserList} />
  );
}

export function InvoiceDetailButton({
  invoice,
  po,
  addPayment,
  adminUserList,
  label = "View",
  children,
  className,
}: {
  invoice: Invoice;
  po: any;
  addPayment: () => void;
  adminUserList: adminUserList[];
  label?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outline" size="sm" className={className} onClick={() => setOpen(true)}>
        {children ?? label}
      </Button>
      <InvoiceViewSheet
        po={po}
        invoice={invoice}
        open={open}
        onOpenChange={setOpen}
        addPayment={addPayment}
        adminUserList={adminUserList}
      />
    </>
  );
}

export function InvoiceViewSheet({ invoice, po, open, onOpenChange, addPayment, adminUserList }: { invoice: Invoice, po: any, open: boolean, onOpenChange: (open: boolean) => void, addPayment: () => void, adminUserList: adminUserList[] }) {
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState(invoice.number || "");
  const [isEditingInvoiceNumber, setIsEditingInvoiceNumber] = useState(false);
  const [invoiceNumberDraft, setInvoiceNumberDraft] = useState("");
  const [isSavingInvoiceNumber, setIsSavingInvoiceNumber] = useState(false);
  const invoiceNumberInputRef = useRef<HTMLInputElement>(null);
  const [supplier, setSupplier] = useState<any>();
  const [customer, setCustomer] = useState<any>();
  const [assetLines, setAssetLines] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [isApplicable, setIsApplicable] = useState<boolean>(false);
  const [summary, setSummary] = useState<string>("");
  const [tdsExempted, setTdsExempted] = useState(Number(invoice.tds_exempted) === 1);
  const [currentStatus, setCurrentStatus] = useState(invoice?.status || "");
  const [proformaInvoiceFiles, setProformaInvoiceFiles] = useState<ShipmentDocFile[]>([]);
  const [taxInvoiceFiles, setTaxInvoiceFiles] = useState<ShipmentDocFile[]>([]);
  const [ewayBillFiles, setEwayBillFiles] = useState<ShipmentDocFile[]>([]);
  const [uploadingShipmentDoc, setUploadingShipmentDoc] = useState<"proformainvoice" | "taxinvoice" | "eway" | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<{ id: string; name: string } | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [openingInvoiceDocId, setOpeningInvoiceDocId] = useState<string | null>(null);
  const [eWayStatus, setEWayStatus] = useState<string | null>(invoice.e_way_status ?? null);
  const [tdsSectionOpen, setTdsSectionOpen] = useState(false);
  const piRef = useRef<HTMLInputElement>(null);
  const tiRef = useRef<HTMLInputElement>(null);
  const ewbRef = useRef<HTMLInputElement>(null);
  const invoiceDocUrlFetchedAtRef = useRef<Record<string, number>>({});

  useEffect(() => {
    setEWayStatus(invoice.e_way_status ?? null);
    setCurrentStatus(invoice?.status || "");
    setTdsExempted(Number(invoice.tds_exempted) === 1);
    setLocalInvoiceNumber(invoice.number || "");
  }, [invoice.id, invoice.e_way_status, invoice.status, invoice.tds_exempted, invoice.number]);

  useEffect(() => {
    if (!isEditingInvoiceNumber) return;
    invoiceNumberInputRef.current?.focus();
    invoiceNumberInputRef.current?.select();
  }, [isEditingInvoiceNumber]);

  const startEditingInvoiceNumber = () => {
    setInvoiceNumberDraft(localInvoiceNumber);
    setIsEditingInvoiceNumber(true);
  };

  const cancelEditingInvoiceNumber = () => {
    setInvoiceNumberDraft(localInvoiceNumber);
    setIsEditingInvoiceNumber(false);
  };

  const saveInvoiceNumber = async () => {
    if (isSavingInvoiceNumber) return;

    const trimmed = invoiceNumberDraft.trim();
    if (!trimmed) {
      toast.error("Invoice number cannot be empty");
      cancelEditingInvoiceNumber();
      return;
    }

    if (trimmed === localInvoiceNumber) {
      setIsEditingInvoiceNumber(false);
      return;
    }

    setIsSavingInvoiceNumber(true);
    try {
      await apiService.post(`/purchase-order/update-invoice-number/${invoice.id}`, {
        number: trimmed,
      });
      setLocalInvoiceNumber(trimmed);
      setIsEditingInvoiceNumber(false);
      toast.success("Invoice number updated");
      if (typeof addPayment === "function") addPayment();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update invoice number");
      cancelEditingInvoiceNumber();
    } finally {
      setIsSavingInvoiceNumber(false);
    }
  };

  const stampInvoiceDocFetchTimes = useCallback((files: ShipmentDocFile[]) => {
    const now = Date.now();
    for (const file of files) {
      invoiceDocUrlFetchedAtRef.current[file.id] = now;
    }
  }, []);


  const applyInvoiceDocsFromPayload = useCallback((payload: unknown) => {
    const root = (payload ?? {}) as Record<string, unknown>;
    const data = ((root.data && typeof root.data === "object" && !Array.isArray(root.data))
      ? root.data
      : root) as Record<string, unknown>;

    const namedProforma = data.proformaInvoiceFiles ?? data.proforma_invoice_files ?? root.proformaInvoiceFiles;
    const namedTax = data.taxInvoiceFiles ?? data.tax_invoice_files ?? root.taxInvoiceFiles;
    const namedEway = data.ewayBillFiles ?? data.eway_bill_files ?? root.ewayBillFiles;
    if (namedProforma != null || namedTax != null || namedEway != null) {
      const proformaFiles = normalizeInvoiceDocFiles(namedProforma);
      const taxFiles = normalizeInvoiceDocFiles(namedTax);
      const ewayFiles = normalizeInvoiceDocFiles(namedEway);
      stampInvoiceDocFetchTimes([...proformaFiles, ...taxFiles, ...ewayFiles]);
      setProformaInvoiceFiles(proformaFiles);
      setTaxInvoiceFiles(taxFiles);
      setEwayBillFiles(ewayFiles);
      return;
    }

    const docs = extractAssetShipmentDocumentsFromResponse(payload);
    if (!docs.length) {
      setProformaInvoiceFiles([]);
      setTaxInvoiceFiles([]);
      setEwayBillFiles([]);
      return;
    }
    const proformaFiles = filterProformaInvoiceDocuments(docs).map((doc) => ({
      id: doc.id,
      name: doc.name,
      url: doc.url,
      updated_at: new Date().toISOString(),
    }));
    const taxFiles = filterTaxInvoiceDocuments(docs).map((doc) => ({
      id: doc.id,
      name: doc.name,
      url: doc.url,
      updated_at: new Date().toISOString(),
    }));
    const ewayFiles = filterEwayBillDocuments(docs).map((doc) => ({
      id: doc.id,
      name: doc.name,
      url: doc.url,
      updated_at: new Date().toISOString(),
    }));
    stampInvoiceDocFetchTimes([...proformaFiles, ...taxFiles, ...ewayFiles]);
    setProformaInvoiceFiles(proformaFiles);
    setTaxInvoiceFiles(taxFiles);
    setEwayBillFiles(ewayFiles);
  }, [stampInvoiceDocFetchTimes]);

  const loadInvoiceTaxAndEwayDocs = useCallback(async () => {
    if (!invoice?.id) {
      setProformaInvoiceFiles([]);
      setTaxInvoiceFiles([]);
      setEwayBillFiles([]);
      return;
    }
    try {
      const response = await apiService.get<unknown>(
        `/disbursement/get-manage-shipment-docs/${encodeURIComponent(String(invoice.id))}`,
      );
      applyInvoiceDocsFromPayload(response);
    } catch (error) {
      console.error("Failed to load invoice tax/e-way documents:", error);
      const inv = invoice as Invoice & {
        proformaInvoiceFiles?: unknown;
        taxInvoiceFiles?: unknown;
        ewayBillFiles?: unknown;
      };
      const proformaFiles = normalizeInvoiceDocFiles(inv.proformaInvoiceFiles);
      const taxFiles = normalizeInvoiceDocFiles(inv.taxInvoiceFiles);
      const ewayFiles = normalizeInvoiceDocFiles(inv.ewayBillFiles);
      stampInvoiceDocFetchTimes([...proformaFiles, ...taxFiles, ...ewayFiles]);
      setProformaInvoiceFiles(proformaFiles);
      setTaxInvoiceFiles(taxFiles);
      setEwayBillFiles(ewayFiles);
    }
  }, [invoice, applyInvoiceDocsFromPayload, stampInvoiceDocFetchTimes]);

  const uploadTaxOrEwayDocs = useCallback(async (files: File[], docsType: "proformainvoice" | "taxinvoice" | "eway") => {
    if (!invoice?.id || !files.length) return;
    const customerId = String(po?.customerId ?? "").trim();
    if (!customerId) {
      toast.error("Customer ID is missing");
      return;
    }
    setUploadingShipmentDoc(docsType);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("invoice_id", String(invoice.id));
      formData.append("customer_id", customerId);
      formData.append("docs_type", docsType);
      files.forEach((file) => formData.append("files", file));
      formData.append("filenames", JSON.stringify(files.map((f) => f.name)));
      await apiService.post("/disbursement/upload-noa-tax-and-eway-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (event.total) {
            setUploadProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      toast.success(`${files.length} file(s) uploaded`);
      if (docsType === "eway" && isEwayNotApplicable(eWayStatus)) {
        try {
          await apiService.post("/disbursement/update-invoice-eway-status", {
            invoice_id: invoice.id,
            e_way_status: null,
          });
          setEWayStatus(null);
        } catch (statusError: any) {
          console.error("Failed to update E-Way status:", statusError);
          toast.error(statusError?.message || "Failed to update E-Way status");
        }
      }
      await loadInvoiceTaxAndEwayDocs();
      if (typeof addPayment === "function") addPayment();
    } catch (error: any) {
      toast.error(error?.message || "Failed to upload files");
    } finally {
      setUploadingShipmentDoc(null);
      setUploadProgress(null);
    }
  }, [invoice?.id, po?.customerId, loadInvoiceTaxAndEwayDocs, addPayment, eWayStatus]);

  const handleDeleteTaxOrEwayDoc = useCallback(async () => {
    if (!deleteDoc) return;
    const userId = getLocalStorageItem("detail")?.userId;
    if (!userId) {
      toast.error("User session not found");
      return;
    }
    setDeletingDoc(true);
    try {
      await apiService.post("/disbursement/delete-shipment-document", { id: deleteDoc.id, userId });
      toast.success("Document deleted");
      setDeleteDoc(null);
      await loadInvoiceTaxAndEwayDocs();
      if (typeof addPayment === "function") addPayment();
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setDeletingDoc(false);
    }
  }, [deleteDoc, loadInvoiceTaxAndEwayDocs, addPayment]);

  const updateInvoiceDocUrl = useCallback((documentId: string, url: string) => {
    const patch = (prev: ShipmentDocFile[]) =>
      prev.map((file) => (file.id === documentId ? { ...file, url } : file));
    setProformaInvoiceFiles(patch);
    setTaxInvoiceFiles(patch);
    setEwayBillFiles(patch);
  }, []);

  const openInvoiceDoc = useCallback(async (file: ShipmentDocFile) => {
    if (!file.id) return;
    let url = file.url;
    const fetchedAt = invoiceDocUrlFetchedAtRef.current[file.id] ?? 0;
    const needsFreshUrl = Date.now() - fetchedAt >= MANAGE_INVOICE_DOC_URL_TTL_MS;
    if (needsFreshUrl) {
      setOpeningInvoiceDocId(file.id);
      try {
        const response = await apiService.get<unknown>(
          `/disbursement/get-manage-invoice-doc/${encodeURIComponent(file.id)}`,
        );
        url = extractManageInvoiceDocUrl(response, file.url);
        invoiceDocUrlFetchedAtRef.current[file.id] = Date.now();
        if (url && url !== file.url) updateInvoiceDocUrl(file.id, url);
      } catch (error) {
        console.error("Failed to refresh invoice document URL:", error);
        toast.error("Failed to refresh document. Opening the saved file.");
      } finally {
        setOpeningInvoiceDocId(null);
      }
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [updateInvoiceDocUrl]);

  useEffect(() => {
    fetchSuppliers();
    fetchCustomer();
    fetchInvoicesAssets();
    fetchPayments();
  }, [po, invoice.id]);

  useEffect(() => {
    if (open && invoice?.id) {
      void loadInvoiceTaxAndEwayDocs();
      void fetchPayments();
      void fetchInvoicesAssets();
    }
  }, [open, invoice?.id, loadInvoiceTaxAndEwayDocs]);

  const fetchSuppliers = async () => {
    if (po?.supplierId) {
      const response = await apiService.get<any>(`/supplier/get-supplier-by-id/${po.supplierId}`);
      setSupplier(response.supplier);
    }
  };
  const fetchCustomer = async () => {
    if (po?.customerId) {
      const response = await apiService.get<any>(`/purchase-order/customer-name/${po.customerId}`);
      setCustomer(response.data);
    }
  };
  const fetchInvoicesAssets = async () => {
    if (invoice.id) {
      const response = await apiService.get<any>(`/purchase-order/get-invoices-assets/${invoice.id}`);
      setAssetLines(Array.isArray(response?.data) ? response.data : []);
    }
  };
  const fetchPayments = async () => {
    if (invoice.id) {
      const response = await apiService.get<any>(`/purchase-order/get-payments/${invoice.id}`);
      setPayments(Array.isArray(response?.data) ? response.data : []);
    }
  };

  const formatCurrency = (n: number) => formatCurrencyINR(n);

  const tdsAmount = invoice.tds_amount || 0;
  const isTdsApplicable = !tdsExempted && tdsAmount > 0;

  const netPayableWithTDS = tdsExempted
    ? (invoice.amount + invoice.tax)
    : (invoice.amount - tdsAmount) + invoice.tax;

  const totalPaid = payments.reduce((sum, p) => sum + parseNumericAmount(p.amount), 0);
  const totalDue = Number(invoice.amount || 0) + Number(invoice.tax || 0);
  const outstanding = Math.max(0, netPayableWithTDS - totalPaid);
  const paidPercent = totalDue > 0 ? Math.min(100, (totalPaid / totalDue) * 100) : 0;
  const currentFY = getFinancialYear(new Date(invoice.date));

  const handleOnPaymentAdded = async (payment: any) => {
    try {
      const response = await apiService.post<ApiResponse<any>>(`/purchase-order/add-payment`, payment);
      await fetchPayments();
      if (response?.data?.status) {
        setCurrentStatus(response.data.status);
      }
      toast.success("Payment added successfully");
      if (typeof addPayment === "function") addPayment();
    } catch (err: any) {
      console.error("Failed to add payment:", err);
      toast.error(err?.message || "Failed to add payment");
      throw err;
    }
  };

  const handleTdsExemptionToggle = async (checked: boolean) => {
    try {
      setTdsExempted(checked);
      await apiService.post(`/purchase-order/update-tds-exempted/${invoice.id}`, { tds_exempted: checked ? 1 : 0 });
      toast.success("TDS exemption updated successfully");
      if (typeof addPayment === "function") addPayment();
    } catch (err) {
      setTdsExempted(!checked);
      toast.error("Failed to update TDS exemption status");
    }
  };

  useEffect(() => { getSummary(invoice); }, [invoice]);

  const getSummary = (invoice: Invoice) => {
    const totalPurchase = invoice.amount + (invoice.previousPurchaseValue || 0);
    if (totalPurchase <= TDS_PURCHASE_THRESHOLD) {
      setSummary(`No TDS applicable. Cumulative purchases (${formatCurrencyINR(totalPurchase)}) are under ₹50 Lakh threshold.`);
    } else {
      setIsApplicable(true);
      if ((invoice.previousPurchaseValue || 0) >= TDS_PURCHASE_THRESHOLD) {
        setSummary(`Threshold already crossed. TDS @ 0.2% will be applied only on the current invoice amount.`);
      } else {
        setSummary(`Threshold limit exceeded. TDS @ 0.2% will be applied only on the amount exceeding the threshold.`);
      }
    }
  };

  const getTimelineEvents = () => {
    const events = [{
      status: 'Proforma Invoice Generated',
      date: invoice.date,
      icon: FileText,
      completed: true,
      description: `Proforma Invoice ${localInvoiceNumber} created`
    }];
    [...payments]
      .sort((a, b) => Number(a.id) - Number(b.id))
      .forEach((p) => {
        events.push({
          status: 'Payment Received',
          date: p.date,
          icon: DollarSign,
          completed: true,
          description: `${formatCurrency(parseNumericAmount(p.amount))}, UTR: ${p.UTR || p.utr || "—"}`
        });
      });
    if (String(currentStatus).toLowerCase() === 'paid') {
      events.push({
        status: 'Fully Paid',
        date: payments[payments.length - 1]?.date || invoice.date,
        icon: CheckCircle2,
        completed: true,
        description: 'Invoice payment completed'
      });
    }
    return events;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        title={`Invoice ${localInvoiceNumber || ""}`}
        className="!w-full sm:!max-w-2xl p-0 border-none shadow-2xl overflow-hidden flex flex-col bg-[#F8FAFC] [&>button.absolute]:hidden"
      >
        
        {/* Header Section */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white shadow-2xs gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E6F7ED] border border-[#DCFCE7] text-[#039855]">
              <ReceiptText className="h-5 w-5" />
            </div>
            <div className="flex items-center gap-2.5 min-w-0">
              <h2 className="text-lg font-semibold text-slate-800 tracking-tight truncate">
                Invoice {localInvoiceNumber}
              </h2>
              <span className={cn(
                "rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0",
                currentStatus.toLowerCase() === 'paid'
                  ? "bg-[#E6F7ED] text-[#039855] border border-[#DCFCE7]"
                  : "bg-[#FFFBEB] text-[#B45309] border border-[#FDE68A]"
              )}>
                {currentStatus.toUpperCase() || 'PENDING'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {outstanding > 0 && (
              <AddPaymentDialog
                invoice={invoice}
                outstanding={outstanding}
                onPaymentAdded={handleOnPaymentAdded}
                trigger={
                  <Button className="bg-[#3F8B98] hover:bg-[#347681] text-white text-xs font-semibold h-8 px-3 rounded-xl shadow-sm flex items-center gap-1.5">
                    <Plus className="h-3.5 w-3.5" />
                    Add Payment
                  </Button>
                }
              />
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <ScrollArea className="flex-1 bg-[#F8FAFC]">
          <div className="p-6 space-y-5">

            {/* Payment summary KPIs */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-start gap-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Paid</p>
                <div className="flex flex-col gap-1 w-full">
                  <h4 className="text-[18px] font-semibold text-emerald-600 tracking-tight">
                    {formatCurrency(totalPaid)}
                  </h4>
                  <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${paidPercent}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#fffbeb] p-4 rounded-2xl border border-[#fef3c7] shadow-sm flex flex-col items-start gap-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#b45309]/50">Outstanding</p>
                <h4 className="text-[18px] font-semibold text-[#dc2626] tracking-tight">
                  {formatCurrency(outstanding)}
                </h4>
              </div>

              <div className="bg-[#f8fafc] p-4 rounded-2xl border border-[#e2e8f0] shadow-sm flex flex-col items-start gap-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total</p>
                <h4 className="text-[18px] font-semibold text-slate-800 tracking-tight">
                  {formatCurrency(totalDue)}
                </h4>
              </div>
            </div>

            {/* Section 1: Invoice Details Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">Invoice Details</h3>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">PO ID</p>
                  <p className="text-xs font-semibold text-slate-800 mt-0.5">{po?.purchaseOrderCode || "ANPL-PO-2608-0179"}</p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">SUPPLIER</p>
                  <p className="text-xs font-semibold text-slate-800 mt-0.5">{supplier?.company_name || "SWIGGY LIMITED"}</p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CUSTOMER</p>
                  <p className="text-xs font-semibold text-slate-800 mt-0.5">{customer?.entityName || "JAIKA MOTORS PRIVATE LIMITED"}</p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">DATE</p>
                  <p className="text-xs font-semibold text-slate-800 mt-0.5">
                    {invoice.date ? new Date(invoice.date).toLocaleDateString("en-GB") : "17/08/2026"}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">DUE DATE</p>
                  <p className="text-xs font-medium text-slate-400 mt-0.5">
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-GB") : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CREATED AT</p>
                  <p className="text-xs font-semibold text-slate-800 mt-0.5">
                    {invoice.date ? new Date(invoice.date).toLocaleDateString("en-GB") : "17/08/2026"}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">CREATED BY</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E0F2FE] text-[#0284C7] font-semibold text-[9px]">
                      JP
                    </span>
                    <span className="text-xs font-semibold text-slate-800">
                      {adminUserList.find(a => String(po?.createdBy) === String(a.id))?.name || "Jagan P"}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">INVOICE NUMBER</p>
                  {isEditingInvoiceNumber ? (
                    <Input
                      ref={invoiceNumberInputRef}
                      value={invoiceNumberDraft}
                      onChange={(e) => setInvoiceNumberDraft(e.target.value)}
                      onBlur={() => void saveInvoiceNumber()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void saveInvoiceNumber();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEditingInvoiceNumber();
                        }
                      }}
                      disabled={isSavingInvoiceNumber}
                      placeholder="Enter invoice number"
                      className="mt-1 h-8 rounded-xl border-slate-200/80 bg-slate-50 px-3 text-xs font-semibold text-slate-800"
                    />
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={startEditingInvoiceNumber}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          startEditingInvoiceNumber();
                        }
                      }}
                      className="mt-1 rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-800 cursor-pointer hover:border-slate-300 hover:bg-white transition-colors"
                    >
                      {localInvoiceNumber || "Click to enter invoice number"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: TDS Section 194Q Card — collapsed by default */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-4">
              <button
                type="button"
                onClick={() => setTdsSectionOpen((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 text-left"
                aria-expanded={tdsSectionOpen}
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-cyan-50 text-[#007A87] font-semibold text-xs">
                    %
                  </span>
                  <h3 className="text-sm font-semibold text-slate-800">TDS Section 194Q</h3>
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold border",
                      tdsExempted
                        ? "bg-slate-50 text-slate-500 border-slate-200"
                        : isTdsApplicable
                          ? "bg-[#FFFBEB] border-[#FDE68A] text-[#B45309]"
                          : "bg-slate-50 text-slate-500 border-slate-200",
                    )}
                  >
                    {tdsExempted ? "TDS Exempted" : isTdsApplicable ? "TDS Applicable" : "TDS Not Applicable"}
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-slate-400 transition-transform",
                      tdsSectionOpen && "rotate-180",
                    )}
                  />
                </div>
              </button>

              {tdsSectionOpen && (
                <>
                  <div className="rounded-2xl border border-[#DCFCE7] bg-[#F0FDF4] p-4 flex items-center justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800">Net Payable Amount</h4>
                      <p className="text-[11px] font-medium text-slate-400 mt-0.5">
                        Base ({formatCurrencyINR(invoice.amount)}) + TDS ({formatCurrencyINR(tdsAmount)}) + GST ({formatCurrencyINR(invoice.tax)})
                      </p>
                    </div>

                    <h4 className="text-xl font-semibold text-[#039855] tracking-tight whitespace-nowrap">
                      {formatCurrencyINR(netPayableWithTDS)}
                    </h4>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-xl border border-slate-100 overflow-hidden">
                      <table className="w-full text-[12px]">
                        <tbody className="divide-y divide-slate-50">
                          {[
                            { label: "Current Purchase Value", value: formatCurrencyINR(invoice.amount) },
                            { label: "Previous Purchase Value", value: formatCurrencyINR(invoice.previousPurchaseValue || 0) },
                            { label: "TDS Applicable", value: tdsExempted ? "Exempted" : isTdsApplicable ? "Yes" : "No" },
                            { label: `Total Purchase Value (FY ${currentFY.year})`, value: formatCurrencyINR(invoice.amount + (invoice.previousPurchaseValue || 0)) },
                            { label: "Exempt Threshold", value: formatCurrencyINR(TDS_PURCHASE_THRESHOLD) },
                            { label: "Taxable Purchase Value (Amount exceeding ₹50 Lakh)", value: formatCurrencyINR(getTaxablePurchaseValue(invoice)) },
                            { label: "TDS Rate", value: "0.1%" },
                            { label: "TDS Amount Payable", value: formatCurrencyINR(tdsAmount) },
                            { label: "GST (18%)", value: formatCurrencyINR(invoice.tax) },
                            { label: "Net Payable Amount", value: formatCurrencyINR(netPayableWithTDS), isBold: true },
                          ].map((row, idx) => (
                            <tr key={idx} className={cn("hover:bg-slate-50/50 transition-colors", row.isBold && "bg-slate-50/80")}>
                              <td className="px-4 py-2.5 text-slate-500 font-medium">{row.label}</td>
                              <td className={cn("px-4 py-2.5 text-right font-medium text-slate-700 whitespace-nowrap", row.isBold && "text-[#007A87] font-bold")}>
                                {row.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {summary && (
                      <div className="flex items-start gap-3 p-4 bg-blue-50/40 rounded-xl border border-blue-100">
                        <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                        <p className="text-[11px] font-medium text-blue-700/80 leading-relaxed italic">{summary}</p>
                      </div>
                    )}

                    {isApplicable && (
                      <div className="flex items-center justify-between p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/60 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="tds-exempt-sheet" className="text-[12px] font-semibold text-slate-700 cursor-pointer flex items-center gap-2">
                            Exempt TDS
                            {isTdsApplicable && <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />}
                          </Label>
                          <p className="text-[10px] font-medium text-slate-400">Toggle to exempt this invoice from TDS deduction</p>
                        </div>
                        <Switch
                          id="tds-exempt-sheet"
                          checked={tdsExempted}
                          onCheckedChange={handleTdsExemptionToggle}
                          disabled={currentStatus?.toLowerCase() !== "pending"}
                          className="data-[state=checked]:bg-[#007A87] data-[state=unchecked]:bg-slate-200 border-none"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Section 3: Proforma / Tax Invoice(s) & E-Way Bill(s) — stacked full-width to match other cards */}
            <div className="space-y-4">
              <InvoiceDocUploadList
                title="Proforma Invoice"
                icon={<FileText className="h-4 w-4 text-slate-500" />}
                files={proformaInvoiceFiles}
                uploading={uploadingShipmentDoc === "proformainvoice"}
                uploadProgress={uploadingShipmentDoc === "proformainvoice" ? uploadProgress : null}
                onUploadClick={() => piRef.current?.click()}
                onRemove={(id, name) => setDeleteDoc({ id, name })}
                onOpenFile={(file) => void openInvoiceDoc(file)}
                openingFileId={openingInvoiceDocId}
              />
              <input
                ref={piRef}
                type="file"
                multiple
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) void uploadTaxOrEwayDocs(files, "proformainvoice");
                  if (piRef.current) piRef.current.value = "";
                }}
              />

              <InvoiceDocUploadList
                title="Tax Invoice(s)"
                icon={<ReceiptText className="h-4 w-4 text-slate-500" />}
                files={taxInvoiceFiles}
                uploading={uploadingShipmentDoc === "taxinvoice"}
                uploadProgress={uploadingShipmentDoc === "taxinvoice" ? uploadProgress : null}
                onUploadClick={() => tiRef.current?.click()}
                onRemove={(id, name) => setDeleteDoc({ id, name })}
                onOpenFile={(file) => void openInvoiceDoc(file)}
                openingFileId={openingInvoiceDocId}
              />
              <input
                ref={tiRef}
                type="file"
                multiple
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) void uploadTaxOrEwayDocs(files, "taxinvoice");
                  if (tiRef.current) tiRef.current.value = "";
                }}
              />

              <InvoiceDocUploadList
                title="E-Way Bill(s)"
                icon={<Truck className="h-4 w-4 text-slate-500" />}
                files={ewayBillFiles}
                uploading={uploadingShipmentDoc === "eway"}
                uploadProgress={uploadingShipmentDoc === "eway" ? uploadProgress : null}
                onUploadClick={() => ewbRef.current?.click()}
                onRemove={(id, name) => setDeleteDoc({ id, name })}
                onOpenFile={(file) => void openInvoiceDoc(file)}
                openingFileId={openingInvoiceDocId}
              />
              <input
                ref={ewbRef}
                type="file"
                multiple
                accept=".pdf, .xlsx, .xls, .csv"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) void uploadTaxOrEwayDocs(files, "eway");
                  if (ewbRef.current) ewbRef.current.value = "";
                }}
              />
            </div>

            {/* Section 4: Asset Details Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-3.5">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">Asset Details</h3>
              </div>

              <div className="rounded-xl border border-slate-200/80 overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 border-b border-slate-200/80">
                      {["ITEM", "DESCRIPTION", "QTY", "UNIT PRICE", "TAX RATE", "TOTAL"].map((h, i) => (
                        <TableHead key={i} className={cn("text-[10px] font-semibold uppercase tracking-wider text-slate-400 h-9 px-4", i === 2 && "text-center", i === 5 && "text-right")}>
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(assetLines.length > 0 ? assetLines : []).map((line, idx) => {
                      const qty = Number(line.qty ?? line.quantity ?? 1) || 1;
                      const unitPrice = Number(line.unitPrice ?? line.unit_price ?? 0) || 0;
                      const taxRate = Number(line.taxRate ?? line.tax_rate ?? 0) || 0;
                      const base = unitPrice * qty;
                      const total = base + (base * taxRate) / 100;
                      return (
                        <TableRow key={idx} className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors">
                          <TableCell className="px-4 py-3 font-semibold text-slate-800 text-xs">{line.item || "—"}</TableCell>
                          <TableCell className="px-4 py-3 text-slate-500 text-xs font-normal">{line.description || line.specification || "—"}</TableCell>
                          <TableCell className="px-4 py-3 text-center font-semibold text-slate-800 text-xs">{qty}</TableCell>
                          <TableCell className="px-4 py-3 font-semibold text-slate-800 text-xs">{formatCurrency(unitPrice)}</TableCell>
                          <TableCell className="px-4 py-3 text-slate-600 text-xs font-medium">{taxRate}%</TableCell>
                          <TableCell className="px-4 py-3 text-right font-semibold text-slate-800 text-xs">{formatCurrency(total)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {assetLines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="px-4 py-6 text-center text-xs text-slate-400">
                          No asset lines found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Section 5: Payment History & Timeline Card */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-5">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">Payment History & Timeline</h3>
              </div>

              <div className="relative space-y-6 pl-1 pb-1">
                {/* Vertical Timeline Active Line connecting green node centers */}
                <div className="absolute left-[13px] top-[14px] bottom-[14px] w-[2px] bg-[#039855]" />

                {getTimelineEvents().map((event, idx) => {
                  const EventIcon = event.icon || FileText;
                  const isPaymentEvent = event.status === 'Payment Received';
                  
                  return (
                    <div key={idx} className="relative pl-11 flex items-start justify-between">
                      {/* Green Solid Circle Icon */}
                      <div className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-[#039855] text-white shadow-2xs z-10">
                        <EventIcon className="h-3.5 w-3.5" />
                      </div>

                      {/* Event Content Details */}
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{event.status}</p>
                        {isPaymentEvent ? (
                          <p className="text-[11px] font-medium text-slate-400 mt-0.5">
                            <span className="font-semibold text-[#039855]">
                              {event.description.includes(", UTR:")
                                ? event.description.split(", UTR:")[0]
                                : event.description}
                            </span>
                            {event.description.includes("UTR:")
                              ? ` • UTR ${event.description.split("UTR:")[1]?.trim()}`
                              : ""}
                          </p>
                        ) : (
                          <p className="text-[11px] font-medium text-slate-400 mt-0.5">{event.description}</p>
                        )}
                      </div>

                      {/* Date Right-Aligned */}
                      <span className="text-xs font-medium text-slate-400 shrink-0 ml-4">
                        {event.date ? new Date(event.date).toLocaleDateString("en-GB") : "17/08/2026"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </ScrollArea>

      </SheetContent>

      <Dialog open={!!deleteDoc} onOpenChange={(o) => { if (!o && !deletingDoc) setDeleteDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Remove <span className="font-medium text-foreground">{deleteDoc?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" disabled={deletingDoc} onClick={() => setDeleteDoc(null)}>Cancel</Button>
            <Button
              variant="destructive"
              className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
              disabled={deletingDoc}
              onClick={() => void handleDeleteTaxOrEwayDoc()}
            >
              {deletingDoc ? "Deleting…" : "OK"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}

function InvoiceDocUploadList({
  title,
  icon,
  files,
  onUploadClick,
  onRemove,
  onOpenFile,
  openingFileId,
  uploading,
  uploadProgress,
  uploadDisabled,
}: {
  title: string;
  icon: React.ReactNode;
  files: ShipmentDocFile[];
  onUploadClick: () => void;
  onRemove: (id: string, name: string) => void;
  onOpenFile?: (file: ShipmentDocFile) => void;
  openingFileId?: string | null;
  uploading?: boolean;
  uploadProgress?: number | null;
  uploadDisabled?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-2xs space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {icon}
          {title}
        </div>
        <Button
          size="sm"
          onClick={onUploadClick}
          disabled={uploading || uploadDisabled}
          className="h-7 shrink-0 rounded-xl bg-[#007A87] hover:bg-[#006666] text-white font-medium text-xs px-3 shadow-2xs gap-1"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>
      {uploading && (
        <div className="space-y-1">
          <Progress value={uploadProgress ?? undefined} className="h-1.5" />
          <p className="text-[11px] text-muted-foreground tabular-nums">
            {uploadProgress != null ? `${uploadProgress}% uploaded` : "Uploading…"}
          </p>
        </div>
      )}
      {files.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-xs font-medium text-slate-400">
          No files uploaded yet
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {files.map((file) => (
            <DocThumb
              key={file.id}
              name={file.name}
              url={file.url}
              updatedAt={file.updated_at}
              opening={openingFileId === file.id}
              onOpen={onOpenFile ? () => onOpenFile(file) : undefined}
              onRemove={() => onRemove(file.id, file.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddPaymentDialog({
  invoice,
  outstanding,
  onPaymentAdded,
  trigger,
}: {
  invoice: any;
  outstanding: number;
  onPaymentAdded: (p: any) => void;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(outstanding.toString());
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [method, setMethod] = useState("Bank Transfer");
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(String(outstanding));
      setDate(new Date().toISOString().split("T")[0]);
      setMethod("Bank Transfer");
      setUtr("");
    }
  }, [open, outstanding]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    if (parsedAmount > outstanding + 0.01) {
      toast.error("Amount cannot exceed outstanding balance");
      return;
    }
    setSubmitting(true);
    try {
      await onPaymentAdded({
        invoiceId: invoice.id,
        amount: parsedAmount,
        date,
        method,
        UTR: utr,
      });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[640px] rounded-[28px] p-7 border-none shadow-2xl bg-white">
        <DialogHeader className="mb-5">
          <DialogTitle className="flex items-center gap-2.5 text-xl font-bold text-slate-800">
            <DollarSign className="h-6 w-6 text-[#3f8b98]" />
            Record Payment
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Amount Paid (₹)
            </Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              max={outstanding}
              step="0.01"
              className="h-12 font-bold text-lg text-[#3f8b98] border-slate-200 focus:border-[#3f8b98] rounded-xl transition-all shadow-sm"
              required
            />
            <p className="text-[10px] text-slate-400 font-medium italic">
              Outstanding: {formatCurrencyINR(outstanding)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Payment Date
              </Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-12 rounded-xl border-slate-200 focus:border-[#3f8b98] font-medium"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="method" className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Payment Method
              </Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger id="method" className="h-12 rounded-xl border-slate-200 focus:border-[#3f8b98] font-medium">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-100 shadow-2xl p-1.5">
                  <SelectItem value="Bank Transfer" className="rounded-lg font-medium text-[13px]">
                    Bank Transfer
                  </SelectItem>
                  <SelectItem value="Check" className="rounded-lg font-medium text-[13px]">
                    Check
                  </SelectItem>
                  <SelectItem value="Cash" className="rounded-lg font-medium text-[13px]">
                    Cash
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="utr" className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              UTR / Reference Number
            </Label>
            <Input
              id="utr"
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              placeholder="e.g. UTR123456789"
              className="h-12 rounded-xl border-slate-200 focus:border-[#3f8b98] font-medium"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#3f8b98] hover:bg-[#347681] text-white font-bold uppercase tracking-widest text-[13px] h-12 mt-2 rounded-xl shadow-lg transition-all active:scale-95"
          >
            {submitting ? "Saving…" : "Confirm Payment"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
